import type { Responsive } from '#types/common'
import type { ButtonVariant } from '#types/components/vf/button'
import type { TextLinkStyle } from '#types/styles'
import type { Target } from '#types/target'

type ShopTheLookCtaTargetInput = TextLinkStyle & {
  ctaStyle?: Responsive<ButtonVariant>
  defaultCtaStyle?: Responsive<ButtonVariant>
  text?: string
}

/**
 * Maps Shop The Look CMS CTA styling fields into the shared `Target` shape used by
 * `cms-shared-button`, using app config as the default source when CMS omits `ctaStyle`.
 * @param input - Shop The Look CTA styling fields from CMS.
 * @param input.ctaStyle - Responsive button/text-link variant selection.
 * @param input.defaultCtaStyle - App-config fallback used when CMS omits `ctaStyle`.
 * @param input.text - Optional CTA label override.
 * @param input.textLinkColor - Responsive text-link color override.
 * @param input.textLinkHoverColor - Text-link hover color override.
 * @param input.textLinkStyle - Text-link typography class override.
 * @param input.textLinkUnderline - Text-link underline behavior.
 * @returns A shared CMS `Target` object.
 * @category Utils
 */
export const getShopTheLookCtaTarget = ({
  ctaStyle,
  defaultCtaStyle,
  text,
  textLinkColor,
  textLinkHoverColor,
  textLinkStyle,
  textLinkUnderline
}: ShopTheLookCtaTargetInput): Target => {
  const style = [ctaStyle, defaultCtaStyle].find((style) => Object.values(style || {}).some(Boolean))

  return {
    text,
    style,
    textLinkColor,
    textLinkHoverColor,
    textLinkStyle,
    textLinkUnderline,
  }
}
